/**
 * Global Export Utility Functions
 * 
 * Provides reusable export functionality for downloading data in various formats
 */

/**
 * Export data as a file download
 * @param {Blob|Object} data - Data to export (Blob for binary files, Object for JSON)
 * @param {String} filename - Name of the file to download
 * @param {String} format - Format type: 'csv', 'xlsx', 'json'
 */
export const downloadFile = (data, filename, format) => {
    let blob;

    if (format === 'json') {
        // For JSON, convert object to string
        const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
        blob = new Blob([jsonString], { type: 'application/json' });
    } else if (format === 'csv') {
        // For CSV, data should already be a Blob or string
        blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
    } else if (format === 'xlsx') {
        // For XLSX, data should be a Blob
        blob = data instanceof Blob ? data : new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
    } else {
        throw new Error(`Unsupported format: ${format}`);
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Extract filename from Content-Disposition header
 * @param {String} contentDisposition - Content-Disposition header value
 * @param {String} defaultFilename - Default filename if header doesn't contain one
 * @returns {String} Filename
 */
export const extractFilenameFromHeader = (contentDisposition, defaultFilename) => {
    if (!contentDisposition) return defaultFilename;
    
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
    if (filenameMatch && filenameMatch[1]) {
        // Remove quotes if present
        return filenameMatch[1].replace(/['"]/g, '');
    }
    
    return defaultFilename;
};

/**
 * Handle export response and trigger download
 * @param {Object} response - API response object
 * @param {String} format - Export format: 'csv', 'xlsx', 'json'
 * @param {String} defaultFilename - Default filename if not in headers
 */
export const handleExportResponse = (response, format, defaultFilename) => {
    try {
        if (format === 'json') {
            // For JSON, response.data should be the JSON object
            const jsonString = JSON.stringify(response.data, null, 2);
            downloadFile(jsonString, defaultFilename, 'json');
        } else {
            // For CSV and XLSX, response.data should be a Blob
            const contentDisposition = response.headers?.['content-disposition'];
            const filename = extractFilenameFromHeader(contentDisposition, defaultFilename);
            downloadFile(response.data, filename, format);
        }
    } catch (error) {
        console.error('Export error:', error);
        throw new Error('Failed to export data. Please try again.');
    }
};

/**
 * Export button component props generator
 * @param {Function} onExport - Export handler function
 * @param {Boolean} isLoading - Loading state
 * @returns {Array} Array of button configurations
 */
export const getExportButtons = (onExport, isLoading = false) => {
    return [
        {
            label: 'Export as CSV',
            format: 'csv',
            className: 'px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed',
            onClick: () => onExport('csv'),
            disabled: isLoading,
        },
        {
            label: 'Export as XLSX',
            format: 'xlsx',
            className: 'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed',
            onClick: () => onExport('xlsx'),
            disabled: isLoading,
        },
        {
            label: 'Export as JSON',
            format: 'json',
            className: 'px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed',
            onClick: () => onExport('json'),
            disabled: isLoading,
        },
    ];
};
